import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { parseInternationalPhoneNumber, getCountryFromPhone } from '../../utils/phoneNumber';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';
import { defaultPhoneCountry } from '../../utils/businessDefaults';

export interface PhoneCardData {
  id: string;
  phoneNumber: string;
  label?: string;
  isPrimary?: boolean;
  isVerified?: boolean;
}

interface PhoneCardProps {
  phone: PhoneCardData;
  onPress?: (phone: PhoneCardData) => void;
  onEdit?: (phone: PhoneCardData) => void;
  onDelete?: (phoneId: string) => void;
  onSetPrimary?: (phoneId: string) => void;
  showActions?: boolean;
}

export const PhoneCard: React.FC<PhoneCardProps> = ({
  phone,
  onPress,
  onEdit,
  onDelete,
  onSetPrimary,
  showActions = true,
}) => {
  const styles = useThemedStyles(getStyles);
  // Format phone number for display
  const formatPhoneNumber = (phoneNumber: string): string => {
    try {
      const phoneToParse = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
      const parsed = parseInternationalPhoneNumber(phoneToParse);
      if (parsed && parsed.isValid) {
        return parsed.formattedNumber || phoneNumber;
      }
    } catch (error) {
      // If parsing fails, return as-is
    }
    return phoneNumber;
  };

  // Get country code for flag display
  const getCountryCode = (phoneNumber: string): string => {
    try {
      const phoneToParse = phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
      return getCountryFromPhone(phoneToParse) || defaultPhoneCountry();
    } catch (error) {
      return defaultPhoneCountry();
    }
  };

  const formattedPhone = formatPhoneNumber(phone.phoneNumber);
  const countryCode = getCountryCode(phone.phoneNumber);

  return (
    <TouchableOpacity
      style={[styles.card, phone.isPrimary && styles.primaryCard]}
      onPress={() => onPress?.(phone)}
      activeOpacity={0.7}
    >
      <View style={styles.cardContent}>
        <View style={styles.phoneInfo}>
          <View style={styles.phoneHeader}>
            <View style={styles.phoneIconWrapper}>
              <Ionicons name="call-outline" size={20} color={phone.isPrimary ? colors.white : colors.primary} />
            </View>
            <View style={styles.phoneDetails}>
              {phone.label && (
                <Text style={[styles.phoneLabel, phone.isPrimary && styles.primaryText]}>
                  {phone.label}
                </Text>
              )}
              <Text style={[styles.phoneNumber, phone.isPrimary && styles.primaryText]}>
                {formattedPhone}
              </Text>
              {phone.isVerified && (
                <View style={styles.verifiedBadge}>
                  <Ionicons name="checkmark-circle" size={14} color={colors.success} />
                  <Text style={styles.verifiedText}>Verified</Text>
                </View>
              )}
            </View>
          </View>
          {phone.isPrimary && (
            <View style={styles.primaryBadge}>
              <Text style={styles.primaryBadgeText}>Primary</Text>
            </View>
          )}
        </View>

        {showActions && (
          <View style={styles.actions}>
            {!phone.isPrimary && onSetPrimary && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onSetPrimary(phone.id);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="star-outline" size={18} color={colors.text.secondary} />
              </TouchableOpacity>
            )}
            {onEdit && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onEdit(phone);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="pencil-outline" size={18} color={colors.text.secondary} />
              </TouchableOpacity>
            )}
            {onDelete && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onDelete(phone.id);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    }),
  },
  primaryCard: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  phoneInfo: {
    flex: 1,
  },
  phoneHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  phoneIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: withAlpha(colors.primary, 0.07),
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneDetails: {
    flex: 1,
  },
  phoneLabel: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.medium,
    marginBottom: 2,
  },
  phoneNumber: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  primaryText: {
    color: colors.white,
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  verifiedText: {
    fontSize: typography.fontSize.xs,
    color: colors.success,
    fontFamily: typography.fontFamily.medium,
  },
  primaryBadge: {
    marginTop: spacing.xs,
    alignSelf: 'flex-start',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.full,
    backgroundColor: withAlpha(colors.white, 0.13),
  },
  primaryBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
});
